import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { formatCurrencyUsdFixedTwo } from '../../helpers/common';
import {
  getBudgetTemplateSheetLoadTabs,
  previewBudgetTemplateSheetLoad,
  saveBudgetTemplateSheetLoad,
  publishBudgetTemplateSheetLoad,
} from '../../slices/quickbooks/budgetSlice';

const formatReadAt = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  const hour = date.getHours();
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()} ${hour % 12 || 12}:${String(date.getMinutes()).padStart(2, '0')} ${hour < 12 ? 'am' : 'pm'}`;
};

const detailText = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};

const FindingList = ({ title, items, tone }) => (
  <section className="rounded border border-gray-200 p-3">
    <h3 className="font-semibold text-gray-800">{title} ({items?.length || 0})</h3>
    {items?.length ? (
      <ul className="mt-2 space-y-1 text-sm">
        {items.map((item, index) => (
          <li key={`${item.code || title}-${index}`} className={`rounded px-2 py-1 ${tone}`}>
            <strong>{item.code || item.kind || 'Finding'}</strong>
            <pre className="mt-1 whitespace-pre-wrap font-sans text-xs">
              {detailText(Object.fromEntries(Object.entries(item).filter(([key]) => key !== 'values')))}
            </pre>
          </li>
        ))}
      </ul>
    ) : <p className="mt-2 text-sm text-gray-500">None</p>}
  </section>
);

const diffGroups = [
  ['added', 'Added'],
  ['removed', 'Removed'],
  ['renamed', 'Renamed'],
  ['typeChanged', 'Type changes'],
  ['categoryChanged', 'Category changes'],
  ['parentChanged', 'Parent changes'],
  ['rollupChanged', 'Roll-up changes'],
  ['formulaChanged', 'Formula changes'],
  ['signChanged', 'Sign changes'],
];

const monthName = (value) => {
  const month = Number(String(value || '').slice(5, 7));
  return Number.isInteger(month) && month >= 1 && month <= 12
    ? new Date(Date.UTC(2026, month - 1, 1)).toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
    : String(value || '');
};

const formatDiffChange = (key, change, categories) => {
  if (key === 'added' || key === 'removed') {
    return `${change.key} · ${change.name || 'Unnamed'} · ${change.type || change.kind || 'row'}`;
  }
  if (key === 'renamed') {
    return `${change.from} · ${change.fromName || 'Unnamed'} → ${change.to} · ${change.toName || 'Unnamed'}`;
  }
  if (key === 'formulaChanged') {
    const terms = (value) => (value?.terms || []).map((term) => `${term.coef} × ${term.key}`).join(' + ') || value?.exprKind || 'input';
    return `${change.key}: ${terms(change.from)} → ${terms(change.to)}`;
  }
  if (key === 'categoryChanged') {
    const categoryName = (id) => categories.find((category) => String(category.id) === String(id))?.name || (id == null ? 'Unmapped' : `Category ${id}`);
    return `${change.key}: ${categoryName(change.from)} → ${categoryName(change.to)}`;
  }
  if (key === 'rollupChanged') {
    return `${change.key}: ${(change.from || []).join(', ') || 'none'} → ${(change.to || []).join(', ') || 'none'}`;
  }
  if (key === 'signChanged') return `${change.key}: ${change.from || 'none'} → ${change.to || 'none'}`;
  if (key === 'typeChanged') return `${change.key}: ${change.from || 'none'} → ${change.to || 'none'}`;
  if (key === 'parentChanged') return `${change.key}: ${change.from || 'none'} → ${change.to || 'none'}`;
  return change.key || 'Structure changed';
};

const DiffSummary = ({ diff, categories }) => {
  const hasChanges = diff && Object.values(diff).some((value) => (
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  ));
  if (!hasChanges) return <p className="mt-2 text-sm text-gray-500">No schema changes.</p>;
  return (
    <ul className="mt-2 space-y-1 text-xs">
      {diffGroups.flatMap(([key, title]) => (diff[key] || []).map((change, index) => (
        <li key={`${key}-${index}`}><strong>{title}:</strong> {formatDiffChange(key, change, categories)}</li>
      )))}
      {(diff.modesChanged || []).map((change) => (
        <li key={`mode-${change.month}`}><strong>Mode:</strong> {monthName(change.month)}: {change.from || '—'} → {change.to || '—'}</li>
      ))}
      {diff.monthsChanged && <li><strong>Months:</strong> month columns changed.</li>}
      {diff.sourceChanged && <li><strong>Source:</strong> spreadsheet or tab changed.</li>}
    </ul>
  );
};

const BudgetTemplateSheetLoadModal = ({
  isOpen,
  canEdit = false,
  budgetId = '',
  budgetCards = [],
  categories = [],
  netIncomeCalculations = [],
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [url, setUrl] = useState('');
  const [tabs, setTabs] = useState([]);
  const [spreadsheetTitle, setSpreadsheetTitle] = useState('');
  const [tab, setTab] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [referenceBudgetId, setReferenceBudgetId] = useState('');
  const [netIncomeCalculationId, setNetIncomeCalculationId] = useState('');
  const [categoryOverrides, setCategoryOverrides] = useState({});
  const [preview, setPreview] = useState(null);
  const [savedTemplate, setSavedTemplate] = useState(null);
  const [acknowledgeSchemaChanges, setAcknowledgeSchemaChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const flattenedLines = useMemo(() => (preview?.lines || []).flatMap((group) => (
    (group.children || []).map((line) => ({ ...line, categoryName: group.categoryName }))
  )), [preview?.lines]);
  const saveDisabled = !canEdit || busy || !preview || preview.blocking?.length > 0
    || (preview.reviewRequired && !acknowledgeSchemaChanges)
    || !templateName.trim();

  useEffect(() => {
    if (!isOpen) return;
    setUrl('');
    setTabs([]);
    setSpreadsheetTitle('');
    setTab('');
    setTemplateName('');
    setReferenceBudgetId(budgetId == null ? '' : String(budgetId));
    setNetIncomeCalculationId('');
    setCategoryOverrides({});
    setPreview(null);
    setSavedTemplate(null);
    setAcknowledgeSchemaChanges(false);
    setError('');
  }, [isOpen, budgetId]);

  if (!isOpen) return null;

  const loadTabs = async () => {
    setBusy(true);
    setError('');
    setPreview(null);
    try {
      const data = await dispatch(getBudgetTemplateSheetLoadTabs({ url })).unwrap();
      setTabs(data.tabs || []);
      setSpreadsheetTitle(data.spreadsheet_title || data.spreadsheetTitle || '');
      const initialTab = data.tabs?.[0]?.title || '';
      setTab(initialTab);
      setTemplateName(initialTab || data.spreadsheetTitle || 'Budget Template');
      setSavedTemplate(null);
    } catch (reason) {
      setError(typeof reason === 'string' ? reason : 'Could not read spreadsheet tabs.');
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    setError('');
    setSavedTemplate(null);
    try {
      const data = await dispatch(previewBudgetTemplateSheetLoad({
        url,
        tab,
        referenceBudgetId: referenceBudgetId || null,
        netIncomeCalculationId: netIncomeCalculationId || null,
        categoryOverrides,
      })).unwrap();
      setPreview(data);
      if (!referenceBudgetId && data.referenceDefaulted && data.referenceBudgetId != null) {
        setReferenceBudgetId(String(data.referenceBudgetId));
      }
      setAcknowledgeSchemaChanges(false);
    } catch (reason) {
      setError(typeof reason === 'string' ? reason : 'Could not preview this sheet.');
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (saveDisabled) return;
    setBusy(true);
    setError('');
    try {
      const data = await dispatch(saveBudgetTemplateSheetLoad({
        url,
        tab,
        name: templateName,
        referenceBudgetId: referenceBudgetId || null,
        netIncomeCalculationId: netIncomeCalculationId || null,
        categoryOverrides,
        expectedFingerprint: preview.fingerprint,
        expectedSourceHash: preview.source.sourceHash,
        acknowledgeSchemaChanges,
      })).unwrap();
      setSavedTemplate(data.template);
      setPreview(data.preview || preview);
      onSaved?.();
    } catch (reason) {
      setError(typeof reason === 'string' ? reason : 'Could not save template draft.');
    } finally {
      setBusy(false);
    }
  };

  const updateCategoryOverride = async (lineKey, value) => {
    const nextOverrides = { ...categoryOverrides };
    if (value === '') delete nextOverrides[lineKey];
    else nextOverrides[lineKey] = Number.isNaN(Number(value)) ? value : Number(value);
    setCategoryOverrides(nextOverrides);
    setBusy(true);
    setError('');
    setSavedTemplate(null);
    try {
      const data = await dispatch(previewBudgetTemplateSheetLoad({
        url,
        tab,
        referenceBudgetId: referenceBudgetId || null,
        netIncomeCalculationId: netIncomeCalculationId || null,
        categoryOverrides: nextOverrides,
      })).unwrap();
      setPreview(data);
      if (!referenceBudgetId && data.referenceDefaulted && data.referenceBudgetId != null) {
        setReferenceBudgetId(String(data.referenceBudgetId));
      }
      setAcknowledgeSchemaChanges(false);
    } catch (reason) {
      setError(typeof reason === 'string' ? reason : 'Could not preview this category change.');
    } finally {
      setBusy(false);
    }
  };

  const publishDraft = async () => {
    const templateId = savedTemplate?.id;
    const expectedFingerprint = savedTemplate?.items?.structure?.fingerprint || preview?.fingerprint;
    if (!canEdit || !templateId || !expectedFingerprint) return;
    setBusy(true);
    setError('');
    try {
      const data = await dispatch(publishBudgetTemplateSheetLoad({
        id: templateId,
        expectedFingerprint,
      })).unwrap();
      setSavedTemplate(data.template);
      onSaved?.();
    } catch (reason) {
      setError(typeof reason === 'string' ? reason : 'Could not publish template.');
    } finally {
      setBusy(false);
    }
  };

  const savedStatus = savedTemplate?.items?.structure?.status;
  const modal = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="sheet-template-title">
      <div className="max-h-[94vh] w-full max-w-7xl overflow-auto rounded-lg bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="sheet-template-title" className="text-xl font-semibold text-gray-900">Load Budget Template from Sheet</h2>
            <p className="mt-1 text-sm text-gray-600">Review the sheet structure before saving a draft template.</p>
          </div>
          <button type="button" className="rounded p-1 text-gray-500 hover:bg-gray-100" onClick={onClose} aria-label="Close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700">Google Sheets URL</label>
            <div className="flex gap-2">
              <input className="min-w-0 flex-1 rounded border border-gray-300 px-3 py-2" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="Paste spreadsheet URL" />
              <button type="button" disabled={busy || !url.trim()} onClick={loadTabs} className="rounded bg-gray-800 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                {busy && !preview ? 'Loading…' : 'Load tabs'}
              </button>
            </div>
            {spreadsheetTitle && <p className="mt-1 text-xs text-gray-500">{spreadsheetTitle}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Sheet tab</label>
            <select className="w-full rounded border border-gray-300 px-3 py-2" value={tab} onChange={(event) => {
              setTab(event.target.value);
              setTemplateName(event.target.value);
            }} disabled={!tabs.length}>
              {tabs.map((sheet) => <option key={sheet.gid} value={sheet.title}>{sheet.title}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Template name</label>
            <input className="w-full rounded border border-gray-300 px-3 py-2" value={templateName} onChange={(event) => setTemplateName(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Reference budget</label>
            <select className="w-full rounded border border-gray-300 px-3 py-2" value={referenceBudgetId} onChange={(event) => setReferenceBudgetId(event.target.value)}>
              <option value="">None</option>
              {budgetCards.map((budget) => <option key={budget.id} value={budget.id}>{budget.name} · {budget.period}</option>)}
            </select>
            {preview?.referenceDefaulted
              && String(referenceBudgetId) === String(preview.referenceBudgetId)
              && <p className="mt-1 text-xs text-gray-500">Defaulted to the latest published budget: {preview.referenceBudgetName}</p>}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Net Income calculation</label>
            <select className="w-full rounded border border-gray-300 px-3 py-2" value={netIncomeCalculationId} onChange={(event) => setNetIncomeCalculationId(event.target.value)}>
              <option value="">Use reference budget default</option>
              {netIncomeCalculations.map((calculation) => <option key={calculation.id} value={calculation.id}>{calculation.name}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <button type="button" disabled={busy || !url.trim() || !tab} onClick={runPreview} className="rounded bg-blue-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {busy && preview ? 'Refreshing preview…' : 'Preview sheet'}
          </button>
        </div>
        {error && <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        {preview && (
          <div className="mt-5 space-y-4">
            <div className="grid gap-3 text-sm md:grid-cols-4">
              <div className="rounded bg-gray-50 p-3"><strong>Variant</strong><div>{preview.variant}</div></div>
              <div className="rounded bg-gray-50 p-3"><strong>Fingerprint</strong><div className="break-all font-mono text-xs">{preview.fingerprint?.slice(0, 12)}</div></div>
              <div className="rounded bg-gray-50 p-3"><strong>Source read</strong><div>{formatReadAt(preview.source?.readAt)}</div><div>{preview.source?.tab} · gid {preview.source?.gid}</div><div className="mt-1 break-all text-xs">{preview.source?.url}</div></div>
              <div className="rounded bg-gray-50 p-3"><strong>Reference budget</strong><div>{preview.referenceBudgetName || 'None selected'}</div></div>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {preview.months?.map((month) => (
                <span key={month.month} className="rounded bg-gray-100 px-2 py-1">
                  {monthName(month.month)} · {month.mode === 'Actual' ? 'A' : month.mode === 'Forecast' ? 'F' : '—'}
                </span>
              ))}
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <FindingList title="Blocking" items={preview.blocking} tone="bg-red-50 text-red-800" />
              <FindingList title="Warnings" items={preview.warnings} tone="bg-amber-50 text-amber-900" />
              <FindingList title="Informational" items={preview.informational} tone="bg-blue-50 text-blue-800" />
            </div>

            <section className="rounded border border-gray-200 p-3">
              <h3 className="font-semibold text-gray-800">Schema diff</h3>
              <p className="mt-1 text-sm">{preview.reviewRequired ? 'Review required before saving.' : 'No schema review required.'}</p>
              <DiffSummary diff={preview.diff} categories={categories} />
              {preview.reviewRequired && (
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={acknowledgeSchemaChanges} onChange={(event) => setAcknowledgeSchemaChanges(event.target.checked)} disabled={!canEdit} />
                  I reviewed and acknowledge these schema changes.
                </label>
              )}
            </section>

            <section className="rounded border border-gray-200 p-3">
              <h3 className="font-semibold text-gray-800">Proposed lines ({flattenedLines.length})</h3>
              <div className="mt-2 max-h-64 overflow-auto">
                <table className="w-full border-collapse text-left text-xs">
                  <thead><tr className="border-b text-gray-500"><th className="p-2">Category</th><th className="p-2">Code</th><th className="p-2">Name</th><th className="p-2">Kind</th><th className="p-2">Type</th><th className="p-2">Parent</th><th className="p-2">Sign</th><th className="p-2">Source row</th><th className="p-2">Roll-up keys</th></tr></thead>
                  <tbody>{flattenedLines.map((line) => (
                    <tr key={line.key} className="border-b border-gray-100">
                      <td className="p-2">
                        <select
                          className="max-w-40 rounded border border-gray-300 px-1 py-1"
                          value={line.categoryId ?? ''}
                          onChange={(event) => updateCategoryOverride(line.key, event.target.value)}
                          disabled={!canEdit || busy}
                          aria-label={`Category for ${line.name}`}
                        >
                          <option value="">Select category</option>
                          {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                        </select>
                        {line.categoryName && <div className="mt-1 text-gray-500">{line.categoryName}</div>}
                      </td>
                      <td className="p-2">{line.code || '—'}</td><td className="p-2">{line.name}</td>
                      <td className="p-2">{line.kind}</td><td className="p-2">{line.type}</td>
                      <td className="p-2">{line.parentKey || '—'}</td><td className="p-2">{line.signProfile || '—'}</td>
                      <td className="p-2">{line.sourceRow ?? '—'}</td><td className="p-2">{(line.rollUp || []).join(', ') || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>

            <div className="grid gap-3 md:grid-cols-2">
              <section className="rounded border border-gray-200 p-3">
                <h3 className="font-semibold text-gray-800">Metrics</h3>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs">{detailText(preview.metrics)}</pre>
              </section>
              <section className="rounded border border-gray-200 p-3">
                <h3 className="font-semibold text-gray-800">Ratios</h3>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs">{detailText(preview.ratios)}</pre>
              </section>
              <section className="rounded border border-gray-200 p-3">
                <h3 className="font-semibold text-gray-800">Checks</h3>
                <div className="mt-2 max-h-64 overflow-auto">
                  <table className="w-full text-left text-xs">
                    <thead><tr className="border-b text-gray-500"><th className="p-1">Check</th><th className="p-1">Month</th><th className="p-1">Sheet</th><th className="p-1">Recomputed</th><th className="p-1">Diff</th><th className="p-1">Status</th></tr></thead>
                    <tbody>{(preview.checks || []).filter((check) => check.kind !== 'net_income_roll_down').map((check, index) => (
                      <tr key={`${check.kind}-${check.month}-${index}`} className="border-b border-gray-100">
                        <td className="p-1">{check.kind}</td><td className="p-1">{check.month}</td>
                        <td className="p-1">{formatCurrencyUsdFixedTwo(check.sheet)}</td>
                        <td className="p-1">{formatCurrencyUsdFixedTwo(check.recomputed ?? check.children)}</td>
                        <td className="p-1">{formatCurrencyUsdFixedTwo(check.diff)}</td>
                        <td className="p-1">{check.ok === false ? 'Mismatch' : check.ok === true ? 'Pass' : 'Info'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </section>
              <section className="rounded border border-gray-200 p-3">
                <h3 className="font-semibold text-gray-800">Reference comparison</h3>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">{detailText(preview.referenceComparison)}</pre>
              </section>
            </div>
            {preview.checks?.some((check) => check.kind === 'net_income_roll_down') && (
              <section className="rounded border border-gray-200 p-3">
                <h3 className="font-semibold text-gray-800">Net Income roll-down</h3>
                <div className="mt-2 grid gap-1 text-xs md:grid-cols-4">
                  {preview.checks.filter((check) => check.kind === 'net_income_roll_down').map((row, index) => (
                    <div key={`${row.month}-${index}`} className="rounded bg-gray-50 p-2">
                      {row.month} · Eadin {formatCurrencyUsdFixedTwo(row.eadin)} · Sheet {formatCurrencyUsdFixedTwo(row.sheet)} · Diff {formatCurrencyUsdFixedTwo(row.diff)}
                    </div>
                  ))}
                </div>
              </section>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <div className="text-sm text-gray-600">
                {preview.unchanged ? 'This source structure is unchanged.' : 'Save stores a DRAFT template; it does not create a budget.'}
                {savedStatus && <span className="ml-2 font-semibold">{savedStatus}</span>}
              </div>
              <div className="flex gap-2">
                <button type="button" className="rounded border border-gray-300 px-4 py-2 text-sm" onClick={onClose}>Close</button>
                {canEdit && savedStatus === 'DRAFT' && (
                  <button type="button" disabled={busy} onClick={publishDraft} className="rounded bg-green-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Publish</button>
                )}
                {canEdit && (
                  <button type="button" disabled={saveDisabled} onClick={saveDraft} className="rounded bg-blue-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                    {busy ? 'Saving…' : 'Save draft'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

export default BudgetTemplateSheetLoadModal;
